import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Upload,
  BadgeCheck,
  Smartphone,
  Building2,
  Landmark,
  Wallet,
  FileText,
  Sparkles,
  ExternalLink,
  ArrowUpRight,
  CheckCircle2,
  ShieldCheck,
  X,
  Copy,
} from "lucide-react";

const SOURCES = [
  {
    id: "annual",
    tier: "Recommended · Free · All 3 bureaus",
    icon: BadgeCheck,
    accent: "text-[#D5FF3F]",
    name: "AnnualCreditReport.com",
    tagline: "The gold standard. All 3 bureaus, government-mandated free, weekly access.",
    url: "https://www.annualcreditreport.com",
    steps: [
      "Click the button below — it opens annualcreditreport.com in a small window so Aura stays visible in the background.",
      "Click 'Request your free credit reports' on their homepage.",
      "Fill in your name, address, SSN, DOB. This info goes only to the bureaus.",
      "Answer their identity-verification questions (based on your credit history).",
      "Choose to view all three: Equifax, Experian, TransUnion.",
      "For each bureau: click 'Print / Save as PDF' — save as EQ.pdf, EX.pdf, TU.pdf.",
      "Come back here and drop the PDFs into the upload zone on the right →",
    ],
    tip: "Save all 3 bureau reports. Any discrepancy between them is automatically disputable as inaccurate reporting.",
  },
  {
    id: "credit-karma",
    tier: "Free · 2 bureaus (EQ + TU)",
    icon: Smartphone,
    accent: "text-[#7CFF9B]",
    name: "Credit Karma",
    tagline: "Fast, free, updated weekly. Doesn't cover Experian.",
    url: "https://www.creditkarma.com/credit/reports",
    steps: [
      "Click the button to open Credit Karma in a new window.",
      "Sign up (or log in) — no credit card ever required.",
      "Click 'Credit Reports' in the top navigation.",
      "Pick either Equifax or TransUnion (do both — one at a time).",
      "Scroll to the bottom and click 'Download report' — or use Ctrl+P / ⌘+P → Save as PDF.",
      "Come back here and drop the PDF into the upload zone.",
    ],
    tip: "Credit Karma uses VantageScore, not FICO. Score may differ, but the item details are 100% accurate for disputing.",
  },
  {
    id: "experian",
    tier: "Free · Experian only",
    icon: Building2,
    accent: "text-[#4B90FF]",
    name: "Experian.com",
    tagline: "The only free way to get Experian data — Credit Karma doesn't cover it.",
    url: "https://usa.experian.com/access",
    steps: [
      "Click to open Experian's free-report portal.",
      "Create a free account (email + password).",
      "Verify identity with SSN + a couple of history questions.",
      "On the dashboard, click 'Credit Report' → 'View full report'.",
      "Print → Save as PDF (Ctrl+P / ⌘+P).",
      "Drop it in Aura's upload zone.",
    ],
    tip: "Experian will heavily push $24.99/mo upgrades. Ignore every upsell — the free report is complete.",
  },
  {
    id: "myequifax",
    tier: "Free · Equifax only",
    icon: Landmark,
    accent: "text-[#FF6B4A]",
    name: "myEquifax",
    tagline: "Equifax's official consumer portal. Also lets you freeze/unfreeze free.",
    url: "https://my.equifax.com",
    steps: [
      "Click to open my.equifax.com in a new window.",
      "Click 'Create a myEquifax Account'.",
      "Register with email + verify identity via SSN and history.",
      "From dashboard, click 'Credit Report'.",
      "Save with Print → Save as PDF.",
      "Drop it in Aura's upload zone.",
    ],
    tip: "While you're in there, enable a security freeze — it's free and blocks identity theft.",
  },
  {
    id: "bank-apps",
    tier: "Instant · Score preview · Not full report",
    icon: Wallet,
    accent: "text-[#A8ADA5]",
    name: "Your bank/card app",
    tagline: "Fastest way to check your score, but usually just a summary — not full disputable items.",
    steps: [
      "Chase → Credit Journey (TransUnion, VantageScore).",
      "Capital One → CreditWise (works even for non-customers).",
      "Discover → Credit Scorecard (Experian FICO 8; non-customers welcome).",
      "American Express → MyCredit Guide (Experian + TransUnion).",
      "Wells Fargo / BofA → FICO Score inside the mobile app.",
      "Great for tracking. For actual disputes, use one of the sources above.",
    ],
    tip: "Perfect for daily tracking of score movement. For dispute work, always start with annualcreditreport.com.",
  },
];

function UploadZone({ file, setFile, text, setText, loading, onSubmit }) {
  return (
    <div className="card-surface p-6" data-testid="scan-inline-upload">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 rounded-full bg-[#7CFF9B] animate-pulse" />
        <div className="overline">Upload zone · always here</div>
      </div>
      <div className="mb-3">
        <label
          htmlFor="inline-scan-file"
          data-testid="inline-scan-dropzone"
          className="block border border-dashed border-[#3A3D42] rounded-sm p-8 text-center cursor-pointer hover:border-[#D5FF3F]/60 hover:bg-[#D5FF3F]/[0.03] transition-colors"
        >
          <Upload size={22} strokeWidth={1.5} className="mx-auto text-[#D5FF3F] mb-3" />
          <div className="font-medium text-[#F5F3EE] text-sm mb-1">
            {file ? file.name : "Drop PDF here or click"}
          </div>
          <div className="text-[10px] text-[#6D716C] font-mono uppercase tracking-widest">
            .pdf · .txt · up to ~40k chars parsed
          </div>
          <input
            id="inline-scan-file"
            type="file"
            accept=".pdf,.txt"
            className="hidden"
            data-testid="inline-scan-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </label>
      </div>
      <div className="text-center text-xs text-[#6D716C] font-mono uppercase tracking-widest mb-3">or</div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste your report text here…"
        rows={5}
        data-testid="inline-scan-text-input"
        className="rounded-sm font-mono text-xs mb-3"
      />
      <Button
        onClick={onSubmit}
        disabled={loading}
        data-testid="inline-scan-submit"
        className="w-full rounded-full btn-lime h-11"
      >
        {loading ? (
          <>Scanning…</>
        ) : (
          <>
            <Sparkles size={13} className="mr-1.5" /> Scan my report
          </>
        )}
      </Button>
      <div className="flex items-start gap-2 mt-3 text-[10px] text-[#6D716C] font-mono uppercase tracking-widest leading-relaxed">
        <ShieldCheck size={11} className="text-[#D5FF3F] flex-shrink-0 mt-0.5" />
        <span>No human ever reads your report. Parsed in-memory by AI only.</span>
      </div>
    </div>
  );
}

function GuideDrawer({ source, onOpenChange, onBackToUpload }) {
  const [opened, setOpened] = useState(false);
  if (!source) return null;
  const Icon = source.icon;

  const openBureau = () => {
    if (!source.url) return;
    const w = 900, h = 720;
    const left = Math.max(0, (window.screen.width - w) / 2);
    const top = Math.max(0, (window.screen.height - h) / 2);
    window.open(
      source.url,
      "aura_bureau",
      `width=${w},height=${h},left=${left},top=${top},noopener,noreferrer`
    );
    setOpened(true);
  };

  const handleBackToUpload = () => {
    onOpenChange(null);
    // Nudge the parent to spotlight the upload zone
    if (onBackToUpload) onBackToUpload(source.id);
  };

  return (
    <Sheet open={!!source} onOpenChange={(v) => { if (!v) { setOpened(false); onOpenChange(null); } }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl bg-[#131518] border-l border-[#26282C] text-[#F5F3EE] overflow-y-auto"
        data-testid={`guide-drawer-${source.id}`}
      >
        <SheetHeader>
          <div className="flex items-start gap-3 mb-2">
            <div className="w-11 h-11 rounded-sm bg-[#D5FF3F]/10 border border-[#D5FF3F]/20 flex items-center justify-center flex-shrink-0">
              <Icon size={18} className={source.accent} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline mb-1">{source.tier}</div>
              <SheetTitle className="font-display text-2xl text-[#F5F3EE] leading-tight">
                {source.name}
              </SheetTitle>
              <SheetDescription className="text-[#A8ADA5] text-sm mt-1">
                {source.tagline}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {source.url && (
          <div className="mt-6 mb-4">
            <Button
              onClick={openBureau}
              data-testid={`guide-open-${source.id}`}
              className="w-full rounded-full btn-lime h-12 px-5"
            >
              Open {source.name} in a small window
              <ExternalLink size={14} className="ml-2" />
            </Button>
            <div className="text-[10px] text-[#6D716C] font-mono uppercase tracking-widest text-center mt-2">
              Opens in a new floating window · Aura stays right here
            </div>
          </div>
        )}

        {/* Back-to-Aura shortcut — appears after user opens the bureau site, or is always shown as an escape hatch */}
        <div className="mb-6">
          <Button
            onClick={handleBackToUpload}
            data-testid={`guide-back-to-upload-${source.id}`}
            className={`w-full rounded-full h-12 px-5 font-medium transition-all ${
              opened
                ? "bg-[#7CFF9B] text-[#0B0C0E] hover:bg-[#93FFAB] shadow-[0_0_28px_rgba(124,255,155,0.35)] animate-pulse"
                : "btn-outline hover:!bg-[#7CFF9B] hover:!text-[#0B0C0E] hover:!border-[#7CFF9B]"
            }`}
          >
            <CheckCircle2 size={16} className="mr-2" strokeWidth={2.5} />
            I&rsquo;ve got my report — take me to upload
          </Button>
          <div className="text-[10px] text-[#6D716C] font-mono uppercase tracking-widest text-center mt-2">
            {opened ? "One tap · straight to Aura&rsquo;s upload zone" : "Skip the guide — jump to upload"}
          </div>
        </div>

        <div className="overline mb-3">Follow along</div>
        <ol className="space-y-3 mb-6">
          {source.steps.map((step, i) => (
            <li key={i} className="flex items-start gap-3 text-sm">
              <span className="ticker text-sm text-[#D5FF3F] mt-0.5 flex-shrink-0 min-w-[24px]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-[#A8ADA5] leading-relaxed">{step}</span>
            </li>
          ))}
        </ol>

        <div className="rounded-sm border border-[#D5FF3F]/20 bg-[#D5FF3F]/5 p-4 flex items-start gap-2 text-xs text-[#A8ADA5]">
          <Sparkles size={13} className="text-[#D5FF3F] flex-shrink-0 mt-0.5" />
          <span>
            <span className="text-[#F5F3EE] font-medium">Pro tip:</span> {source.tip}
          </span>
        </div>

        <div className="mt-8 pt-6 border-t border-[#26282C]">
          <div className="text-[10px] text-[#6D716C] font-mono uppercase tracking-widest mb-2">
            Universal shortcut · save any page as PDF
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { p: "Windows", k: "Ctrl + P" },
              { p: "Mac", k: "⌘ + P" },
            ].map((x) => (
              <div key={x.p} className="rounded-sm border border-[#26282C] p-2 bg-[#0B0C0E]">
                <div className="overline mb-0.5">{x.p}</div>
                <div className="ticker text-sm text-[#D5FF3F]">{x.k}</div>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-[#6D716C] font-mono uppercase tracking-widest mt-2">
            In the print dialog, set Destination to &ldquo;Save as PDF&rdquo;
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function GetReport() {
  const navigate = useNavigate();
  const [openSource, setOpenSource] = useState(null);
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [spotlightUpload, setSpotlightUpload] = useState(false);
  const uploadRef = useRef(null);

  const handleBackToUpload = () => {
    setSpotlightUpload(true);
    // Scroll upload zone into view; mobile-friendly.
    setTimeout(() => {
      uploadRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    // Fade the spotlight ring back off after 6s
    setTimeout(() => setSpotlightUpload(false), 6000);
    toast.success("Drop your PDF into the upload zone — Aura&apos;s ready");
  };

  const submit = async () => {
    if (!file && !text.trim()) {
      toast.error("Drop a PDF or paste your report text first");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      if (text.trim()) fd.append("text", text);
      const { data } = await api.post("/scan/report", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const bureauMsg = data.detected_bureau
        ? data.detected_bureau === "tri_merge"
          ? " · Tri-merge detected"
          : ` · ${data.detected_bureau.charAt(0).toUpperCase() + data.detected_bureau.slice(1)} detected`
        : "";
      toast.success(`Found ${data.items.length} disputable items${bureauMsg}`);
      navigate("/app/items");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-10">
        <div className="overline mb-3 text-[#D5FF3F]">Get your report · stay on Aura</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">
          Pull your file
          <br />
          <span className="text-[#D5FF3F]">without leaving.</span>
        </h1>
        <p className="text-[#A8ADA5] mt-3 max-w-2xl leading-relaxed">
          Pick a source below — Aura opens it in a small floating window so we&rsquo;re still here
          guiding you. When your PDF is saved, drop it in the upload zone on the right and Aura&rsquo;s
          AI takes over.
        </p>
      </div>

      {/* Two-column layout: sources on left, sticky upload on right */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3" data-testid="report-sources">
          {SOURCES.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setOpenSource(s)}
                data-testid={`source-open-${s.id}`}
                className="w-full text-left card-surface p-5 flex items-start gap-4 group hover:border-[#D5FF3F]/40 transition-colors"
              >
                <div className="w-11 h-11 rounded-sm bg-[#D5FF3F]/10 border border-[#D5FF3F]/20 flex items-center justify-center flex-shrink-0">
                  <Icon size={18} className={s.accent} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="overline mb-1">{s.tier}</div>
                  <div className="font-display text-2xl leading-tight">{s.name}</div>
                  <div className="text-sm text-[#A8ADA5] mt-1">{s.tagline}</div>
                </div>
                <div className="flex items-center gap-2 text-xs font-mono text-[#6D716C] group-hover:text-[#D5FF3F] flex-shrink-0 mt-1">
                  Guide me
                  <ArrowUpRight size={13} />
                </div>
              </button>
            );
          })}

          {/* Not sure card */}
          <div className="card-surface p-5 border-[#D5FF3F]/20 bg-[#D5FF3F]/[0.02]">
            <div className="flex items-start gap-3">
              <FileText size={18} className="text-[#D5FF3F] flex-shrink-0 mt-1" />
              <div>
                <div className="font-medium mb-1">Not sure? Go with the first option.</div>
                <div className="text-sm text-[#A8ADA5]">
                  AnnualCreditReport.com gives you all 3 bureaus in one visit and is the same source
                  every credit-repair pro uses. 10-minute process, zero cost.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side: sticky upload */}
        <div
          ref={uploadRef}
          className={`lg:sticky lg:top-6 self-start transition-all duration-500 ${
            spotlightUpload ? "ring-4 ring-[#7CFF9B]/60 rounded-lg shadow-[0_0_60px_rgba(124,255,155,0.35)]" : ""
          }`}
          data-testid="upload-zone-container"
        >
          <UploadZone
            file={file}
            setFile={setFile}
            text={text}
            setText={setText}
            loading={loading}
            onSubmit={submit}
          />
        </div>
      </div>

      <GuideDrawer source={openSource} onOpenChange={setOpenSource} onBackToUpload={handleBackToUpload} />
    </AppShell>
  );
}
